/**
 * API Client for Support Portal
 */

import { authService } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

interface DashboardMetrics {
  active_users: {
    current: number;
    today: number;
    week: number;
    month: number;
  };
  crashes: {
    today: number;
    week: number;
    by_version: Record<string, number>;
    by_platform: Record<string, number>;
  };
  top_errors: Array<{
    error_message: string;
    count: number;
    affected_users: number;
  }>;
  versions: Array<{
    version: string;
    build_number?: string;
    platform?: string;
    users: number;
    crashes: number;
  }>;
  activity: {
    stops_today: number;
    photos_today: number;
    scans_today: number;
  };
}

/** Which app a device / crash / log came from (CORE-8116). */
type AppFlavor = 'locate2u' | 'fedex';

interface UserListItem {
  device_id: string;
  user_id?: string;
  email?: string;
  driver_id?: string;
  team_id?: string;
  team_name?: string;
  platform: string;
  app_version: string;
  build_number?: string;
  device_model: string;
  last_seen: string;
  is_online: boolean;
  is_moving: boolean;
  recent_crashes: number;
  last_crash?: string;
  /** Optional so a portal talking to a worker without CORE-8116 still renders (→ Locate2u). */
  app_flavor?: AppFlavor;
}

interface UserListResponse {
  users: UserListItem[];
  total: number;
  page: number;
  pages: number;
}

interface CrashReport {
  id: string;
  device_id: string;
  user_id?: string;
  session_id?: string;
  error_type: string;
  error_name?: string;
  error_message: string;
  stack_trace?: string;
  component_stack?: string;
  screen_name?: string;
  action?: string;
  app_version: string;
  platform: string;
  os_version?: string;
  device_model?: string;
  device_manufacturer?: string;
  memory_usage?: number;
  battery_level?: number;
  is_charging?: boolean;
  network_type?: string;
  location?: {
    latitude: number;
    longitude: number;
    speed?: number;
  };
  latitude?: number;
  longitude?: number;
  is_moving?: boolean;
  custom_data?: Record<string, unknown>;
  status: string;
  notes?: string;
  resolved_at?: string;
  resolved_by?: string;
  occurred_at: string;
  reported_at: string;
  app_flavor?: AppFlavor;
}

interface CrashListResponse {
  crashes: CrashReport[];
  total: number;
  page: number;
  pages: number;
}

interface UserDetail {
  is_online: boolean;
  is_moving: boolean;
  device: {
    id: string;
    user_id?: string;
    email?: string;
    driver_id?: string;
    team_id?: string;
    team_name?: string;
    platform: string;
    os_version?: string;
    device_model?: string;
    app_version: string;
    build_number?: string;
    app_flavor?: AppFlavor;
    first_seen_at: string;
    last_seen_at: string;
    last_location_lat?: number;
    last_location_lng?: number;
    battery_level?: number;
  };
  current_session?: {
    id: string;
    started_at: string;
  };
  recent_sessions: Array<{
    id: string;
    started_at: string;
    ended_at?: string;
    duration_seconds?: number;
  }>;
  recent_crashes: CrashReport[];
  recent_activity: Array<{
    id: string;
    event_type: string;
    event_data?: Record<string, unknown>;
    occurred_at: string;
  }>;
  stats: {
    total_sessions: number;
    total_crashes: number;
    avg_session_duration: number;
    stops_completed_today: number;
  };
}

interface CrashDetail {
  crash: CrashReport;
  device: {
    id: string;
    email?: string;
    driver_id?: string;
    team_name?: string;
    platform: string;
    app_version: string;
    device_manufacturer?: string;
  };
  session?: {
    id: string;
    started_at: string;
    initial_route?: string;
    connection_type?: string;
  };
  breadcrumbs: Array<{
    timestamp: string;
    type: string;
    category?: string;
    message: string;
    data?: Record<string, unknown>;
  }>;
  similar_crashes: Array<{
    id: string;
    device_id: string;
    occurred_at: string;
  }>;
}

interface LiveActivity {
  active_users: number;
  recent_crashes: Array<{
    id: string;
    device_id: string;
    error_message: string;
    occurred_at: string;
  }>;
  recent_activity: Array<{
    id: string;
    device_id: string;
    event_type: string;
    occurred_at: string;
  }>;
}

interface DiagnosticsFile {
  driverId: string;
  date: string;
  key: string;
  size: number;
  uploaded: string;
  appFlavor?: AppFlavor;
}

interface DiagnosticsListResponse {
  files: DiagnosticsFile[];
  total: number;
  page: number;
  pages: number;
  userInfo?: Record<string, { email?: string; name?: string }>;
  /** The worker hit its listing bound — not every matching log is included. */
  truncated?: boolean;
}

interface DriverDiagnosticsResponse {
  driverId: string;
  files: DiagnosticsFile[];
  total: number;
  /** The worker hit its listing bound for this driver — not every log is included. */
  truncated?: boolean;
}

interface DiagnosticsFileContent {
  driverId: string;
  date: string;
  appFlavor?: AppFlavor;
  size: number;
  uploaded: string;
  content: string;
}

interface FailedSyncItem {
  syncId: string;
  type: string;
  stopId?: string;
  error: string;
  reason: string;
  timestamp: string;
  retryCount: number;
  payload?: Record<string, unknown>;
}

interface DriverDeadLetterStats {
  driverId: string;
  email?: string;
  totalItems: number;
  items: FailedSyncItem[];
}

interface FailedSyncsResponse {
  drivers: DriverDeadLetterStats[];
  summary: {
    totalItems: number;
    byReason: Record<string, number>;
  };
  total: number;
  page: number;
  pages: number;
}

interface HealthCheckResult {
  healthy: boolean;
  latencyMs: number;
  statusCode?: number;
  error?: string;
  timestamp: string;
}

interface SystemHealthResponse {
  circuitStatus: 'closed' | 'open' | 'half_open' | 'unknown';
  consecutiveFailures: number;
  lastCheck: HealthCheckResult | null;
  lastStateChange: string | null;
  openedAt: string | null;
  totalFailures: number;
  totalSuccesses: number;
  locate2uApiUrl: string;
  environment: string;
  timestamp: string;
}

class ApiClient {
  private async getAuthHeader(): Promise<string> {
    const token = await authService.getValidAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }
    return `Bearer ${token}`;
  }

  private async fetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const authHeader = await this.getAuthHeader();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader,
        ...options?.headers,
      },
    });

    if (response.status === 401) {
      // Token refresh failed or user logged out
      authService.logout();
      window.location.href = '/login';
      throw new Error('Session expired');
    }

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    return response.json();
  }

  // Dashboard
  async getDashboard(): Promise<DashboardMetrics> {
    return this.fetch<DashboardMetrics>('/api/portal/dashboard');
  }

  // Teams
  async getTeams(): Promise<{ teams: string[] }> {
    return this.fetch<{ teams: string[] }>('/api/portal/teams');
  }

  // Users
  async getUsers(params?: {
    status?: string;
    platform?: string;
    version?: string;
    team_name?: string;
    app?: AppFlavor;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<UserListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.platform) searchParams.set('platform', params.platform);
    if (params?.version) searchParams.set('version', params.version);
    if (params?.team_name) searchParams.set('team_name', params.team_name);
    if (params?.app) searchParams.set('app', params.app);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());

    return this.fetch<UserListResponse>(`/api/portal/users?${searchParams}`);
  }

  async getUserDetail(deviceId: string): Promise<UserDetail> {
    return this.fetch<UserDetail>(`/api/portal/users/${deviceId}`);
  }

  // Crashes
  async getCrashes(params?: {
    status?: string;
    version?: string;
    platform?: string;
    error_type?: string;
    app?: AppFlavor;
    from?: string;
    to?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<CrashListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.version) searchParams.set('version', params.version);
    if (params?.platform) searchParams.set('platform', params.platform);
    if (params?.error_type) searchParams.set('error_type', params.error_type);
    if (params?.app) searchParams.set('app', params.app);
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());

    return this.fetch<CrashListResponse>(`/api/portal/crashes?${searchParams}`);
  }

  async getCrashDetail(crashId: string): Promise<CrashDetail> {
    return this.fetch<CrashDetail>(`/api/portal/crashes/${crashId}`);
  }

  async updateCrash(crashId: string, data: { status: string; notes?: string }): Promise<void> {
    await this.fetch(`/api/portal/crashes/${crashId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Live Activity
  async getLiveActivity(): Promise<LiveActivity> {
    return this.fetch<LiveActivity>('/api/portal/activity/live');
  }

  // Diagnostics
  async getDiagnostics(params?: {
    driver_id?: string;
    date?: string;
    app?: AppFlavor;
    page?: number;
    limit?: number;
    /** Every matching file in one response, unpaginated (used to build the folder view). */
    all?: boolean;
  }): Promise<DiagnosticsListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.all) searchParams.set('all', '1');
    if (params?.driver_id) searchParams.set('driver_id', params.driver_id);
    if (params?.date) searchParams.set('date', params.date);
    if (params?.app) searchParams.set('app', params.app);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());

    return this.fetch<DiagnosticsListResponse>(`/api/portal/diagnostics?${searchParams}`);
  }

  async getDriverDiagnostics(driverId: string): Promise<DriverDiagnosticsResponse> {
    return this.fetch<DriverDiagnosticsResponse>(`/api/portal/diagnostics/${driverId}`);
  }

  /** `appFlavor: 'fedex'` reads the FedEx copy of that day's log (CORE-8116). */
  async getDiagnosticsFile(
    driverId: string,
    date: string,
    appFlavor?: AppFlavor
  ): Promise<DiagnosticsFileContent> {
    const query = appFlavor === 'fedex' ? '?app=fedex' : '';
    return this.fetch<DiagnosticsFileContent>(`/api/portal/diagnostics/${driverId}/${date}${query}`);
  }

  // Failed Syncs
  async getFailedSyncs(params?: {
    reason?: string;
    page?: number;
    limit?: number;
  }): Promise<FailedSyncsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.reason) searchParams.set('reason', params.reason);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());

    return this.fetch<FailedSyncsResponse>(`/api/portal/failed-syncs?${searchParams}`);
  }

  async getDriverDeadLetterStats(driverId: string): Promise<DriverDeadLetterStats> {
    return this.fetch<DriverDeadLetterStats>(`/api/portal/drivers/${driverId}/dead-letter-stats`);
  }

  async requestTokenRefresh(driverId: string): Promise<{ success: boolean; message: string }> {
    return this.fetch(`/api/portal/drivers/${driverId}/request-token-refresh`, {
      method: 'POST',
    });
  }

  async retryDriverDeadLetter(driverId: string): Promise<{ success: boolean; message: string; recovered?: number }> {
    return this.fetch(`/api/portal/drivers/${driverId}/retry-dead-letter`, {
      method: 'POST',
    });
  }

  // Request logs from device (sends push notification if available)
  async requestLogs(deviceId: string): Promise<{
    success: boolean;
    message: string;
    requestId: string;
    notificationSent?: boolean;
    pushToken?: string | null;
    pushError?: string;
    debug?: {
      hasPushToken: boolean;
      tokenFormat: string | null;
    };
  }> {
    return this.fetch(`/api/portal/users/${deviceId}/request-logs`, {
      method: 'POST',
    });
  }

  // System Health
  async getSystemHealth(): Promise<SystemHealthResponse> {
    return this.fetch<SystemHealthResponse>('/api/portal/system/health');
  }
}

export const apiClient = new ApiClient();
export type {
  AppFlavor,
  DashboardMetrics,
  UserListItem,
  UserListResponse,
  CrashReport,
  CrashListResponse,
  UserDetail,
  CrashDetail,
  LiveActivity,
  DiagnosticsFile,
  DiagnosticsListResponse,
  DriverDiagnosticsResponse,
  DiagnosticsFileContent,
  FailedSyncItem,
  FailedSyncsResponse,
  DriverDeadLetterStats,
  SystemHealthResponse,
};
